import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, Typography, useMediaQuery, useTheme, alpha } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Location, MagicStar } from 'iconsax-react';
import CustomDialog from '@ui/components/common/Dialog';
import BDrawer from '@ui/components/common/BDrawer';
import AppAlert from '@ui/components/common/Alert';
import SearchFilter from '@ui/components/Button/SearchFilter';
import { Icon } from '@iconify/react';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { getPickupLocationsNotPaginatedByEventIdAsync } from '@application/features/pickupLocation/pickupLocationThunk';
import { FamilyFestGetPickupLocationsNotPaginatedAsync } from '@application/features/familyFest/familyFestPickupLocationThunk';
function getCapacityColor(theme, obtained, total) {
    if (obtained > total)
        return 'error';
    if (obtained === total)
        return 'success';
    return 'info';
}
const ReassignPickupLocationDialog = ({ open, onClose, onConfirm, options, pickupLocationId, isFamilyFest }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    const { eventId } = useParams();
    const dispatch = useAppDispatch();
    const { pickupLocationsList: standardPickupLocations } = useAppSelector((state) => state.pickupLocation);
    const { familyFestPickupLocationsList } = useAppSelector((state) => state.familyFestPickupLocation);
    const pickupLocationsList = isFamilyFest
        ? familyFestPickupLocationsList
        : standardPickupLocations;
    const [searchValue, setSearchValue] = useState('');
    const [selectedId, setSelectedId] = useState(null);
    // Fetch pickup locations when dialog opens
    useEffect(() => {
        if (open && eventId && !options) {
            if (isFamilyFest) {
                dispatch(FamilyFestGetPickupLocationsNotPaginatedAsync({
                    includeClosed: false,
                    searchString: searchValue || undefined,
                    pickupLocationId: pickupLocationId || undefined
                }));
            }
            else {
                dispatch(getPickupLocationsNotPaginatedByEventIdAsync({
                    eventId,
                    includeClosed: false,
                    searchString: searchValue || undefined,
                    pickupLocationId: pickupLocationId || undefined
                }));
            }
        }
    }, [open, eventId, dispatch, options, searchValue, pickupLocationId, isFamilyFest]);
    // Map PickupLocationDto to ReassignPickupLocationItem
    const mappedOptions = useMemo(() => {
        if (options)
            return options;
        if (!pickupLocationsList)
            return [];
        return pickupLocationsList.map((location) => ({
            id: location.id,
            name: location.name,
            address: location.address,
            eventName: location.eventName || '',
            capacity: {
                obtained: location.assignedAttendees ?? location.assignedCount ?? 0,
                total: location.totalCapacity ?? 0
            }
        }));
    }, [options, pickupLocationsList]);
    const selected = mappedOptions.find((o) => o.id === selectedId);
    const handleConfirm = () => {
        if (selected)
            onConfirm?.(selected);
        setSelectedId(null);
        onClose();
    };
    const handleClose = () => {
        setSearchValue('');
        setSelectedId(null);
        onClose();
    };
    const dialogActions = (_jsxs(_Fragment, { children: [_jsx(Button, { variant: 'outlined', color: 'inherit', onClick: handleClose, size: 'large', fullWidth: true, children: t('actions.cancel') }), _jsx(Button, { variant: 'contained', color: 'inherit', size: 'large', fullWidth: true, disabled: selectedId == null, onClick: handleConfirm, endIcon: _jsx(Icon, { icon: 'lucide:check' }), children: t('actions.confirm') })] }));
    const content = (_jsxs(Box, { display: 'flex', flexDirection: 'column', gap: 2, children: [_jsx(AppAlert, { severity: 'info', children: t('events.eventDetails.transportTab.reassignPickupLocationAlert') }), _jsx(SearchFilter, { forceDesktopVariant: true, fullWidth: true, searchValue: searchValue, onSearchChange: setSearchValue, placeholder: t('events.eventDetails.transportTab.searchPickupLocations'), breakpoint: 'lg', onClear: () => setSearchValue(''), widthBox: '100%', bgVariant: 'grey8' }), _jsxs(Box, { sx: {
                    maxHeight: 'calc(100dvh - 400px)',
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5
                }, children: [mappedOptions.map((opt) => {
                        const isSelected = selectedId === opt.id;
                        const capColor = getCapacityColor(theme, opt.capacity.obtained, opt.capacity.total);
                        return (_jsxs(Box, { onClick: () => setSelectedId(opt.id), sx: {
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1,
                                p: 2,
                                borderRadius: 3,
                                border: '1px solid',
                                borderColor: isSelected ? 'primary.main' : alpha(theme.palette.grey[500], 0.24),
                                bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.08) : 'background.paper',
                                cursor: 'pointer',
                                '&:hover': {
                                    bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.12) : 'action.hover'
                                }
                            }, children: [_jsx(Box, { display: 'flex', alignItems: 'center', gap: 1, children: _jsx(Chip, { size: 'medium', label: `${opt.capacity.obtained}/${opt.capacity.total} ${t('events.eventDetails.transportTab.seats')}`, color: capColor, variant: 'soft', sx: { fontWeight: 500 } }) }), _jsx(Typography, { variant: 'body1', children: opt.name }), _jsxs(Box, { display: 'flex', alignItems: 'center', gap: 0.5, children: [_jsx(Location, { variant: 'Outline', size: 16, color: theme.palette.text.secondary }), _jsx(Typography, { variant: 'caption', color: 'text.secondary', children: opt.address })] }), _jsxs(Box, { display: 'flex', alignItems: 'center', gap: 0.5, children: [_jsx(MagicStar, { variant: 'Outline', size: 16, color: theme.palette.text.secondary }), _jsx(Typography, { variant: 'caption', color: 'text.secondary', children: opt.eventName })] })] }, opt.id));
                    }), mappedOptions.length === 0 && (_jsx(Typography, { variant: 'body2', color: 'text.secondary', textAlign: 'center', py: 2, children: t('dataGrid.noResults') }))] })] }));
    if (isSmallScreen) {
        return (_jsx(BDrawer, { open: open, onClose: handleClose, title: t('events.eventDetails.transportTab.reassignPickupLocation'), anchor: 'bottom', showBottomBar: true, dialogActions: dialogActions, children: _jsx(Box, { sx: { p: 2 }, children: content }) }));
    }
    return (_jsx(CustomDialog, { open: open, onClose: handleClose, titleWithIcon: t('events.eventDetails.transportTab.reassignPickupLocation'), titleIcon: '/assets/images/location-pin.png', maxWidth: 'sm', backgroundVariant: 'white', closeIcon: true, actions: dialogActions, children: _jsx(Box, { p: 2, children: content }) }));
};
export default ReassignPickupLocationDialog;
